/**
 * Creator Editor Component
 * Simplified content editor for creators with IBM Carbon Design System
 */

import React, { useState, useEffect } from 'react';
import {
  Form,
  FormGroup,
  TextInput,
  TextArea,
  Select,
  SelectItem,
  Button,
  Tag,
  Breadcrumb,
  BreadcrumbItem,
  InlineNotification,
  Toggle,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Modal,
  ProgressIndicator,
  ProgressStep,
  Stack,
  Tile,
} from '@carbon/react';
import { Save, Send, View, Template, Time, CheckmarkFilled, ArrowLeft } from '@carbon/icons-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styles from './CreatorEditor.module.scss';

interface ContentData {
  id?: string;
  title: string;
  type: 'blog' | 'article' | 'tutorial' | 'guide';
  content: string;
  excerpt: string;
  tags: string[];
  seoTitle: string;
  seoDescription: string;
  status: 'draft' | 'pending_review' | 'approved' | 'published';
  template?: string;
}

interface Template {
  id: string;
  name: string;
  sections: Array<{
    title: string;
    placeholder: string;
    required: boolean;
  }>;
}

interface CreatorEditorProps {
  mode?: 'create' | 'edit';
}

const CreatorEditor: React.FC<CreatorEditorProps> = ({ mode }) => {
  const navigate = useNavigate();
  const { contentId } = useParams();
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get('template');

  const [contentData, setContentData] = useState<ContentData>({
    title: '',
    type: 'blog',
    content: '',
    excerpt: '',
    tags: [],
    seoTitle: '',
    seoDescription: '',
    status: 'draft',
  });

  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [autoSave, setAutoSave] = useState(true);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [notification, setNotification] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(
    null
  );
  const [showPreview, setShowPreview] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const [readingTime, setReadingTime] = useState(0);

  const templates: { [key: string]: Template } = {
    'blog-standard': {
      id: 'blog-standard',
      name: 'Standard Blog Post',
      sections: [
        { title: 'Introduction', placeholder: 'Hook your readers with an engaging opening...', required: true },
        { title: 'Main Content', placeholder: 'Develop your main points with supporting details...', required: true },
        { title: 'Conclusion', placeholder: 'Summarize key takeaways...', required: true },
        { title: 'Call to Action', placeholder: 'What should readers do next?', required: false },
      ],
    },
    'tutorial-step': {
      id: 'tutorial-step',
      name: 'Step-by-Step Tutorial',
      sections: [
        { title: 'Overview', placeholder: 'What will readers learn?', required: true },
        { title: 'Prerequisites', placeholder: 'What do readers need to know or have?', required: true },
        { title: 'Steps', placeholder: 'Step 1: ...\nStep 2: ...\nStep 3: ...', required: true },
        { title: 'Summary', placeholder: 'Recap what was accomplished...', required: true },
        { title: 'Resources', placeholder: 'Additional learning materials...', required: false },
      ],
    },
  };

  useEffect(() => {
    if (contentId && contentId !== 'new') {
      loadContent(contentId);
    } else if (templateId && templates[templateId]) {
      applyTemplate(templates[templateId]);
    }
  }, [contentId, templateId]);

  useEffect(() => {
    const words = contentData.content.split(/\s+/).filter(word => word.length > 0).length;
    setWordCount(words);
    setReadingTime(Math.ceil(words / 200)); // Average reading speed
  }, [contentData.content]);

  useEffect(() => {
    if (autoSave && contentData.content) {
      const timer = setTimeout(() => {
        handleSave(true);
      }, 30000); // Auto-save every 30 seconds

      return () => clearTimeout(timer);
    }
  }, [contentData, autoSave]);

  const loadContent = async (id: string) => {
    try {
      const response = await fetch(`/api/v1/creator/content/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setContentData(data);
      }
    } catch (error) {
      console.error('Failed to load content:', error);
      setNotification({ type: 'error', message: 'Failed to load content' });
    }
  };

  const applyTemplate = (template: Template) => {
    const templateContent = template.sections
      .map(section => `## ${section.title}\n\n${section.placeholder}\n\n`)
      .join('');

    setContentData(prev => ({
      ...prev,
      content: templateContent,
      template: template.id,
    }));
  };

  const handleSave = async (isAutoSave: boolean = false) => {
    setSaving(true);
    try {
      const url = contentId && contentId !== 'new' ? `/api/v1/creator/content/${contentId}` : '/api/v1/creator/content';

      const method = contentId && contentId !== 'new' ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contentData),
      });

      if (response.ok) {
        const result = await response.json();
        if (!contentId || contentId === 'new') {
          navigate(`/creator/editor/${result.id}`, { replace: true });
        }
        setLastSaved(new Date());
        if (!isAutoSave) {
          setNotification({ type: 'success', message: 'Content saved successfully' });
        }
      }
    } catch (error) {
      console.error('Failed to save content:', error);
      setNotification({ type: 'error', message: 'Failed to save content' });
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitForApproval = async () => {
    setSubmitting(true);
    try {
      // First save the content
      await handleSave();

      // Then submit for approval
      const response = await fetch(`/api/v1/creator/content/${contentId}/submit`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });

      if (response.ok) {
        setContentData(prev => ({ ...prev, status: 'pending_review' }));
        setNotification({ type: 'success', message: 'Content submitted for approval' });
        setShowSubmitModal(false);
        setTimeout(() => navigate('/creator'), 2000);
      }
    } catch (error) {
      console.error('Failed to submit for approval:', error);
      setNotification({ type: 'error', message: 'Failed to submit for approval' });
    } finally {
      setSubmitting(false);
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !contentData.tags.includes(tagInput.trim())) {
      setContentData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setContentData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove),
    }));
  };

  return (
    <div className={styles.creatorEditor}>
      <div className={styles.editorHeader}>
        <Breadcrumb>
          <BreadcrumbItem href="/creator">Creator Dashboard</BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>
            {contentId && contentId !== 'new' ? 'Edit Content' : 'New Content'}
          </BreadcrumbItem>
        </Breadcrumb>

        <div className={styles.headerActions}>
          {lastSaved && (
            <span className={styles.lastSaved}>
              <Time /> Last saved: {lastSaved.toLocaleTimeString()}
            </span>
          )}
          <Toggle
            id="auto-save"
            size="sm"
            labelText="Auto-save"
            labelA="Off"
            labelB="On"
            toggled={autoSave}
            onToggle={setAutoSave}
          />
          <Button kind="tertiary" renderIcon={View} onClick={() => setShowPreview(true)}>
            Preview
          </Button>
          <Button kind="secondary" renderIcon={Save} onClick={() => handleSave(false)} disabled={saving}>
            {saving ? 'Saving...' : 'Save Draft'}
          </Button>
          <Button
            kind="primary"
            renderIcon={Send}
            onClick={() => setShowSubmitModal(true)}
            disabled={!contentData.title || !contentData.content}
          >
            Submit for Approval
          </Button>
        </div>
      </div>

      {notification && (
        <InlineNotification
          kind={notification.type}
          title={notification.type === 'success' ? 'Success' : 'Error'}
          subtitle={notification.message}
          onCloseButtonClick={() => setNotification(null)}
        />
      )}

      <div className={styles.editorContent}>
        <Tabs>
          <TabList>
            <Tab>Content</Tab>
            <Tab>SEO & Meta</Tab>
            <Tab>Settings</Tab>
          </TabList>
          <TabPanels>
            <TabPanel>
              <Form>
                <Stack gap={6}>
                  <TextInput
                    id="title"
                    labelText="Title"
                    placeholder="Enter a compelling title"
                    value={contentData.title}
                    onChange={e => setContentData({ ...contentData, title: e.target.value })}
                    required
                  />

                  <Select
                    id="type"
                    labelText="Content Type"
                    value={contentData.type}
                    onChange={e => setContentData({ ...contentData, type: e.target.value as any })}
                  >
                    <SelectItem value="blog" text="Blog Post" />
                    <SelectItem value="article" text="Article" />
                    <SelectItem value="tutorial" text="Tutorial" />
                    <SelectItem value="guide" text="Guide" />
                  </Select>

                  <TextArea
                    id="excerpt"
                    labelText="Excerpt"
                    placeholder="Brief description of your content (appears in previews)"
                    value={contentData.excerpt}
                    onChange={e => setContentData({ ...contentData, excerpt: e.target.value })}
                    rows={3}
                  />

                  <FormGroup legendText="Main Content">
                    <TextArea
                      id="content"
                      labelText=""
                      placeholder="Start writing your content..."
                      value={contentData.content}
                      onChange={e => setContentData({ ...contentData, content: e.target.value })}
                      rows={20}
                      required
                    />
                    <div className={styles.contentStats}>
                      <span>{wordCount} words</span>
                      <span>~{readingTime} min read</span>
                    </div>
                  </FormGroup>

                  <FormGroup legendText="Tags">
                    <div className={styles.tagInput}>
                      <TextInput
                        id="tag-input"
                        labelText=""
                        placeholder="Add tags for categorization"
                        value={tagInput}
                        onChange={e => setTagInput(e.target.value)}
                        onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addTag())}
                      />
                      <Button size="sm" onClick={addTag}>
                        Add Tag
                      </Button>
                    </div>
                    <div className={styles.tags}>
                      {contentData.tags.map(tag => (
                        <Tag key={tag} type="blue" filter onClose={() => removeTag(tag)}>
                          {tag}
                        </Tag>
                      ))}
                    </div>
                  </FormGroup>
                </Stack>
              </Form>
            </TabPanel>

            <TabPanel>
              <Form>
                <Stack gap={6}>
                  <TextInput
                    id="seo-title"
                    labelText="SEO Title"
                    placeholder="Title for search engines (50-60 characters)"
                    value={contentData.seoTitle}
                    onChange={e => setContentData({ ...contentData, seoTitle: e.target.value })}
                    maxLength={60}
                  />
                  <TextArea
                    id="seo-description"
                    labelText="SEO Description"
                    placeholder="Description for search engines (150-160 characters)"
                    value={contentData.seoDescription}
                    onChange={e => setContentData({ ...contentData, seoDescription: e.target.value })}
                    rows={3}
                    maxLength={160}
                  />
                </Stack>
              </Form>
            </TabPanel>

            <TabPanel>
              <Tile>
                <h4>Content Settings</h4>
                <div className={styles.settingsInfo}>
                  <p>
                    Status: <Tag type={contentData.status === 'draft' ? 'gray' : 'blue'}>{contentData.status}</Tag>
                  </p>
                  {contentData.template && <p>Template: {templates[contentData.template]?.name}</p>}
                </div>
              </Tile>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </div>

      {/* Preview Modal */}
      <Modal
        open={showPreview}
        onRequestClose={() => setShowPreview(false)}
        modalHeading="Content Preview"
        passiveModal
        size="lg"
      >
        <div className={styles.preview}>
          <h1>{contentData.title || 'Untitled'}</h1>
          <div className={styles.previewMeta}>
            <Tag type="gray">{contentData.type}</Tag>
            <span>{readingTime} min read</span>
          </div>
          {contentData.excerpt && <p className={styles.excerpt}>{contentData.excerpt}</p>}
          <div className={styles.previewContent}>
            {contentData.content.split('\n').map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>
        </div>
      </Modal>

      {/* Submit for Approval Modal */}
      <Modal
        open={showSubmitModal}
        onRequestClose={() => setShowSubmitModal(false)}
        modalHeading="Submit for Approval"
        primaryButtonText="Submit"
        secondaryButtonText="Cancel"
        danger={false}
        onRequestSubmit={handleSubmitForApproval}
        onSecondarySubmit={() => setShowSubmitModal(false)}
        primaryButtonDisabled={submitting}
      >
        <p>
          Are you ready to submit this content for approval? Once submitted, you won't be able to edit it until it's
          reviewed.
        </p>
        <ProgressIndicator currentIndex={1} spaceEqually>
          <ProgressStep label="Create" complete />
          <ProgressStep label="Submit" current />
          <ProgressStep label="Review" />
          <ProgressStep label="Publish" />
        </ProgressIndicator>
      </Modal>
    </div>
  );
};

export default CreatorEditor;
